import React from 'react';
import { Card, CardContent, Typography, Chip, Box, Divider, useTheme } from '@mui/material';
import { FoodRoute } from '../../types';

interface FoodRouteCardProps {
  route: FoodRoute;
  onClick: (route: FoodRoute) => void;
  isSelected: boolean;
}

const FoodRouteCard: React.FC<FoodRouteCardProps> = ({ route, onClick, isSelected }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'commercial':
        return { color: 'success' as const, label: '工业化生产' };
      case 'traditional':
        return { color: 'warning' as const, label: '传统工艺' };
      case 'modern':
        return { color: 'info' as const, label: '现代工艺' };
      default:
        return { color: 'default' as const, label: status };
    }
  };

  const statusConfig = getStatusConfig(route.status);

  return (
    <Card
      onClick={() => onClick(route)}
      sx={{
        cursor: 'pointer',
        height: '100%',
        border: isSelected 
          ? '3px solid' 
          : '1px solid',
        borderColor: isSelected 
          ? (isDarkMode ? '#66bb6a' : '#2e7d32')
          : (isDarkMode ? '#444444' : '#e0e0e0'),
        backgroundColor: isSelected 
          ? (isDarkMode ? '#1b5e20' : '#f1f8e9')
          : (isDarkMode ? '#1e1e1e' : '#ffffff'),
        transition: 'all 0.3s ease',
        '&:hover': {
          borderColor: isDarkMode ? '#81c784' : '#4caf50',
          backgroundColor: isDarkMode ? '#2e7d32' : '#f9fbe7',
        },
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              color: isDarkMode ? '#66bb6a' : '#2e7d32', 
              fontWeight: 600 
            }}
          >
            {route.name}
          </Typography>
          <Chip
            label={statusConfig.label}
            color={statusConfig.color}
            size="small"
          />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
          {route.description}
        </Typography>

        <Divider sx={{ my: 1.5, borderColor: isDarkMode ? '#444444' : '#e0e0e0' }} />

        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            原料:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {route.feedstock.map((item) => (
              <Chip key={item} label={item} size="small" color="primary" variant="outlined" />
            ))}
          </Box>
        </Box>

        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            产品:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {route.products.map((item) => (
              <Chip key={item} label={item} size="small" color="success" variant="outlined" />
            ))}
          </Box>
        </Box>

        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            副产品:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {route.byproducts.map((item) => (
              <Chip key={item} label={item} size="small" color="default" variant="outlined" />
            ))}
          </Box>
        </Box>

        <Divider sx={{ my: 1.5, borderColor: isDarkMode ? '#444444' : '#e0e0e0' }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              发酵类型:
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: isDarkMode ? '#66bb6a' : '#2e7d32', 
                fontWeight: 600 
              }}
            >
              {route.fermentationType}
            </Typography>
          </Box>
          {route.temperature && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                温度:
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: isDarkMode ? '#ffb74d' : '#ff9800', 
                  fontWeight: 600 
                }}
              >
                {route.temperature}
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            生产周期:
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: isDarkMode ? '#81d4fa' : '#0288d1', 
              fontWeight: 600 
            }}
          >
            {route.productionCycle}
          </Typography>
        </Box>

        {route.keyMicroorganisms && route.keyMicroorganisms.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
              关键微生物:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {route.keyMicroorganisms.map((item) => (
                <Chip 
                  key={item} 
                  label={item} 
                  size="small" 
                  sx={{ 
                    backgroundColor: isDarkMode ? '#4a148c' : '#f3e5f5',
                    color: isDarkMode ? '#e1bee7' : '#7b1fa2',
                    fontSize: '0.7rem',
                  }} 
                />
              ))}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default FoodRouteCard;
